import { useEffect, useMemo, useState } from 'react';
import { BookOpen, Search, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const openPopup = (e, url) => {
  e.preventDefault();
  const w = Math.round(window.screen.availWidth * 0.5);
  const h = Math.round(window.screen.availHeight * 0.6);
  const left = Math.round((window.screen.availWidth - w) / 2);
  const top = Math.round((window.screen.availHeight - h) / 2);
  window.open(url, '_blank', `width=${w},height=${h},left=${left},top=${top},noopener,noreferrer`);
};

export default function LiveManualPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState('');

  const load = (refresh = false) => {
    setLoading(true);
    fetch(`${API}/manual/live${refresh ? '?refresh=true' : ''}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => { setData(d); setError(false); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const sections = useMemo(() => {
    if (!data?.content) return [];
    return data.content
      .split(/^_{5,}\s*$/m)
      .map(s => s.trim())
      .filter(Boolean)
      .map((body, i) => {
        const lines = body.split('\n');
        const heading = (lines[0] || '').trim().slice(0, 120);
        return { id: i, heading, body };
      });
  }, [data]);

  const filtered = useMemo(() => {
    if (!search) return sections;
    const q = search.toLowerCase();
    return sections.filter(s => s.body.toLowerCase().includes(q));
  }, [sections, search]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200" data-testid="live-manual-page">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-6">
          <BookOpen className="w-8 h-8 text-emerald-400 mx-auto mb-3" />
          <h1 className="text-2xl sm:text-3xl font-black text-white mb-2">
            Federation Manual — <span className="text-emerald-400">Live Edition</span>
          </h1>
          <p className="text-sm text-slate-400">The complete Immutable HumanCodex NextXus — synced from the master document</p>
          <div className="flex items-center justify-center gap-3 mt-2 text-[10px] text-slate-600">
            <span className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> LIVE
            </span>
            {data && <span>{Math.round(data.chars / 1000)}k characters</span>}
            {data && (
              <a href={data.source_url} onClick={(e) => openPopup(e, data.source_url)}
                className="flex items-center gap-1 text-slate-500 hover:text-emerald-400 transition-colors"
                data-testid="manual-source-link">
                <ExternalLink className="w-3 h-3" /> Master Document
              </a>
            )}
            <button onClick={() => load(true)} className="flex items-center gap-1 text-slate-500 hover:text-emerald-400 transition-colors"
              data-testid="manual-refresh-btn">
              <RefreshCw className="w-3 h-3" /> Re-sync
            </button>
          </div>
        </div>

        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input placeholder="Search the manual..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9 bg-slate-900/60 border-slate-800/40 text-sm h-9" data-testid="live-manual-search" />
        </div>

        {loading && !data ? (
          <div className="text-center py-20"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-500" /></div>
        ) : error && !data ? (
          <div className="text-center py-20 text-sm text-slate-500" data-testid="live-manual-error">
            Manual source temporarily unavailable. <Button size="sm" variant="outline" onClick={() => load(true)} className="ml-2 h-7 border-slate-700 text-xs">Retry</Button>
          </div>
        ) : (
          <>
            <div className="text-[10px] text-slate-600 mb-3">{filtered.length} of {sections.length} sections{search && ` matching "${search}"`}</div>
            <div className="space-y-4">
              {filtered.map(sec => (
                <div key={sec.id} className="bg-slate-900/30 border border-slate-800/30 rounded-lg p-5" data-testid={`manual-section-${sec.id}`}>
                  <div className="text-xs text-slate-400 leading-relaxed whitespace-pre-wrap">{sec.body}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
